import type { AuthedRequest } from '../auth/AuthProvider';

/** Recordings kept on the device itself (DVR/NVR or camera storage, ADR-020). */
export interface DeviceSpan {
  from: string;
  to: string;
  /** Recording trigger reported by the device ("timing", "motion"…). */
  type: string;
}

export type DeviceClockState = 'ok' | 'invalid' | 'skewed';

export interface DeviceRecordings {
  ok: boolean;
  error_code: string | null;
  /** Playable from a chosen time over RTSP even when the search fails (ADR-041). */
  direct_replay?: boolean;
  spans: DeviceSpan[];
  /** The device clock's last check (null until checked). */
  clock: { state: DeviceClockState; skew_seconds: number } | null;
}

const base = (id: string) => `/api/v1/devices/${encodeURIComponent(id)}/device-recordings`;

export const deviceRecordingsKey = (id: string, from: number, to: number) =>
  ['device-recordings', id, from, to] as const;

export const searchDeviceRecordings = (req: AuthedRequest, id: string, from: number, to: number) =>
  req<DeviceRecordings>(
    'GET',
    `${base(id)}?from=${encodeURIComponent(new Date(from).toISOString())}&to=${encodeURIComponent(new Date(to).toISOString())}`,
    undefined,
    { timeoutMs: 30_000 },
  );

export const startDeviceWebRTC = (
  req: AuthedRequest,
  id: string,
  at: number,
  sdp: string,
  signal: AbortSignal,
) =>
  req<{ sdp: string }>(
    'POST',
    `${base(id)}/webrtc`,
    { at: new Date(at).toISOString(), sdp },
    { signal, timeoutMs: 20_000 },
  );

export const startDeviceHLS = (req: AuthedRequest, id: string, at: number, signal: AbortSignal) =>
  req<{ url: string }>(
    'POST',
    `${base(id)}/hls`,
    { at: new Date(at).toISOString() },
    { signal, timeoutMs: 20_000 },
  );

export const searchErrorLabels: Record<string, string> = {
  unreachable: 'O equipamento não respondeu na porta web. Confira a porta HTTP cadastrada.',
  timeout: 'O equipamento demorou demais para responder à busca.',
  unauthorized: 'Usuário ou senha recusados pelo equipamento.',
  not_supported:
    'O equipamento não oferece busca de gravações (ISAPI ou ONVIF Profile G) para este canal.',
  protocol_error: 'Resposta inesperada do equipamento.',
  blocked: 'Conexão bloqueada pela política de rede.',
};
